import { useQuery } from "@tanstack/react-query";
import {
  ContextoDisparo,
  DisparoConfiguracao,
  DisparoTemplateItem,
  DisparosTotais,
  ListarDisparoTemplatesParams,
  ListarDisparosParams,
  ModeloMeta,
  PaginaDisparos,
} from "@/models/consignado/disparo.model";
import {
  buscarModeloMeta,
  listarDisparoTemplates,
  listarDisparos,
  obterDisparo,
  obterDisparoTemplate,
  resumirDisparos,
} from "@/services/consignado/disparo.service";

// GET /disparos — grade paginada no servidor. A resposta é enxuta de
// propósito (nome, ativo, contexto, gatilho, canais); filtro, mensagem e
// auditoria só vêm no detalhe.
export const DISPAROS_QUERY_KEY = "disparos";

export function useDisparos(params: ListarDisparosParams) {
  return useQuery<PaginaDisparos>({
    queryKey: [DISPAROS_QUERY_KEY, params],
    queryFn: () => listarDisparos(params),
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

/**
 * Preenche o template do canal quando o disparo veio só com `templateId`.
 *
 * O vínculo é vivo: ao salvar, o backend troca a mensagem do canal pela do
 * template, e o detalhe já devolve o template atual. O normal é o canal ter
 * os dois: o `templateId` como vínculo e o `template` como conteúdo.
 *
 * Registro gravado fora do cadastro pode ter só o id. Aí o formulário abria
 * vazio: sem identificador, sem parâmetros e sem variáveis, porque ele lê
 * apenas o objeto embutido. Este hook busca o template e devolve o disparo já
 * completo, mantendo `draftDeDisparo` sem saber que houve resgate.
 */
export function useDisparoHidratado(disparo: DisparoConfiguracao | null | undefined) {
  const canal = disparo?.canais?.[0];
  const faltaTemplate = Boolean(canal?.templateId && !canal?.template);

  const template = useQuery<DisparoTemplateItem>({
    queryKey: [DISPARO_TEMPLATE_QUERY_KEY, canal?.templateId],
    queryFn: () => obterDisparoTemplate(canal!.templateId!),
    enabled: faltaTemplate,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  if (!disparo) return { disparo: null, isLoading: false, isError: false };
  if (!faltaTemplate) return { disparo, isLoading: false, isError: false };
  if (!template.data) {
    return { disparo, isLoading: template.isLoading, isError: template.isError };
  }

  const canais = [...disparo.canais];
  canais[0] = { ...canais[0], template: template.data.conteudo };
  return { disparo: { ...disparo, canais }, isLoading: false, isError: false };
}

/**
 * O template reutilizável vinculado ao canal — para exibir o nome e conferir
 * se o vínculo ainda vale (ativo, mesma tripla). Sem id, não busca nada.
 */
export function useTemplateDeOrigem(templateId?: string) {
  return useQuery<DisparoTemplateItem>({
    queryKey: [DISPARO_TEMPLATE_QUERY_KEY, templateId],
    queryFn: () => obterDisparoTemplate(templateId!),
    enabled: Boolean(templateId),
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

/** Modelo aprovado na Meta: diz se as variáveis são por posição ou por nome e o que cada parâmetro alimenta. */
export function useModeloMeta(nome: string, idioma: string, habilitado: boolean) {
  return useQuery<ModeloMeta>({
    queryKey: ["modelo-meta", nome, idioma],
    queryFn: () => buscarModeloMeta(nome, idioma),
    enabled: habilitado && Boolean(nome),
    retry: 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// GET /disparos/resumo — a faixa de tags acima da tabela. Conta as regras
// cadastradas, não execuções. Precisa de chamada própria porque a grade é
// paginada e a listagem devolve só `total`: somar os ativos da página daria
// o número da página, não o do conjunto.
//
// A chave começa com DISPAROS_QUERY_KEY de propósito — a invalidação que o
// container já faz depois de criar, editar, ativar ou excluir atualiza a
// faixa junto, sem código extra.
export function useResumoDisparos(contexto?: ContextoDisparo) {
  return useQuery<DisparosTotais>({
    queryKey: [DISPAROS_QUERY_KEY, "resumo", contexto ?? null],
    queryFn: () => resumirDisparos(contexto),
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

// GET /disparos/:id — a configuração completa, que é o que o formulário
// precisa pra editar. Também é daqui que sai a `versao` usada no PUT, no
// PATCH de ativação e no DELETE.
export const DISPARO_QUERY_KEY = "disparo";

export function useDisparo(id: string | null) {
  return useQuery<DisparoConfiguracao>({
    queryKey: [DISPARO_QUERY_KEY, id],
    queryFn: () => obterDisparo(id as string),
    enabled: !!id,
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

// GET /disparos/templates — templates reutilizáveis. Sem paginação: o
// endpoint devolve a lista inteira já filtrada, com o conteúdo completo de
// cada template, então a edição abre direto da linha, sem GET por id.
//
// Chave separada de DISPARO_TEMPLATE_QUERY_KEY (singular) para a listagem
// poder ser invalidada sem derrubar o template de origem aberto num disparo.
export const DISPARO_TEMPLATES_QUERY_KEY = "disparo-templates";
export const DISPARO_TEMPLATE_QUERY_KEY = "disparo-template";

export function useDisparoTemplates(params: ListarDisparoTemplatesParams) {
  return useQuery<DisparoTemplateItem[]>({
    queryKey: [DISPARO_TEMPLATES_QUERY_KEY, params],
    queryFn: () => listarDisparoTemplates(params),
    retry: 0,
    refetchOnWindowFocus: false,
  });
}
